import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
function ReadinessScoreVisual() {
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  const motionValue = useMotionValue(0.71);
  const displayValue = useTransform(motionValue, v => v.toFixed(2));
  const [currentValue, setCurrentValue] = useState('0.71');
  const circumference = 2 * Math.PI * 70;
  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
      const controls = animate(motionValue, 0.9, {
        duration: 2.5,
        ease: 'easeOut',
        delay: 0.5
      });
      const unsubscribe = displayValue.on('change', v => setCurrentValue(v));
      return () => {
        controls.stop();
        unsubscribe();
      };
    }
  }, [isInView, hasAnimated, motionValue, displayValue]);
  const progress = hasAnimated ? 0.9 : 0.71;
  const strokeDashoffset = circumference - progress * circumference;
  return <div ref={ref} className="relative flex flex-col items-center" data-id="element-261">
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-tiq-green/10 rounded-full blur-[60px]" data-id="element-262" />

      <div className="relative" data-id="element-263">
        <svg width="200" height="200" viewBox="0 0 200 200" data-id="element-264">
          {/* Track */}
          <circle cx="100" cy="100" r="70" fill="none" stroke="rgba(51,65,85,0.4)" strokeWidth="8" data-id="element-265" />
          {/* Starting position indicator */}
          <circle cx="100" cy="100" r="70" fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth="8" strokeDasharray={circumference} strokeDashoffset={circumference - 0.71 * circumference} strokeLinecap="round" transform="rotate(-90 100 100)" data-id="element-266" />
          {/* Progress */}
          <motion.circle cx="100" cy="100" r="70" fill="none" stroke="#10b981" strokeWidth="8" strokeLinecap="round" strokeDasharray={circumference} initial={{
          strokeDashoffset: circumference - 0.71 * circumference
        }} animate={hasAnimated ? {
          strokeDashoffset
        } : {}} transition={{
          duration: 2.5,
          ease: 'easeOut',
          delay: 0.5
        }} transform="rotate(-90 100 100)" data-id="element-267" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-268">
          <span className="text-4xl font-bold font-mono text-white" data-id="element-269">
            {currentValue}
          </span>
          <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider mt-1" data-id="element-270">
            Readiness Score
          </span>
        </div>
      </div>

      {/* Before/After labels */}
      <div className="flex items-center gap-8 mt-6" data-id="element-271">
        <div className="text-center" data-id="element-272">
          <span className="text-xs font-mono text-slate-500 uppercase tracking-wider block" data-id="element-273">
            Before
          </span>
          <span className="text-lg font-mono text-slate-400 font-semibold" data-id="element-274">
            0.71
          </span>
        </div>
        <div className="flex items-center gap-1" data-id="element-275">
          <div className="w-8 h-px bg-tiq-green/50" data-id="element-276" />
          <svg width="12" height="12" viewBox="0 0 12 12" className="text-tiq-green" data-id="element-277">
            <path d="M2 6h8M7 3l3 3-3 3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-278" />
          </svg>
        </div>
        <div className="text-center" data-id="element-279">
          <span className="text-xs font-mono text-tiq-green uppercase tracking-wider block" data-id="element-280">
            After
          </span>
          <span className="text-lg font-mono text-tiq-green font-bold" data-id="element-281">
            0.90
          </span>
        </div>
      </div>

      {/* Explainability Blurb */}
      <motion.div className="mt-8 bg-slate-800/50 border border-slate-700 rounded-lg p-4 max-w-xs text-center" initial={{
      opacity: 0,
      y: 10
    }} animate={hasAnimated ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      delay: 2
    }} data-id="element-282">
        <p className="text-xs text-slate-300 leading-relaxed" data-id="element-283">
          <span className="text-tiq-green font-semibold" data-id="element-284">
            Ready for Deployment:
          </span>{' '}
          Team Alpha exceeds the 0.85 threshold. Fatigue risks mitigated via
          rotation.
        </p>
      </motion.div>
    </div>;
}
export function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const gains = ['Real-time readiness scoring', 'Capability & certification mapping', 'Predictive deployment risk visibility', 'Performance continuity tracking', 'Better deployment decisions'];
  return <section ref={ref} className="w-full bg-slate-950 py-24 lg:py-32" data-id="element-285">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-286">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center" data-id="element-287">
          {/* Left — Visual */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="order-2 lg:order-1" data-id="element-288">
            <ReadinessScoreVisual data-id="element-289" />
          </motion.div>

          {/* Right — Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8
        }} className="order-1 lg:order-2" data-id="element-290">
            <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight tracking-tight mb-6" data-id="element-291">
              Measure Readiness Before Risk Appears.
            </h2>

            <p className="text-lg text-slate-300 leading-relaxed mb-8" data-id="element-292">
              TalentIQ combines contextual data, human insight, and explainable
              intelligence to give leaders a clear readiness picture.
            </p>

            <p className="text-sm font-mono text-tiq-purple uppercase tracking-wider mb-4" data-id="element-293">
              What You Gain:
            </p>

            <ul className="space-y-3" data-id="element-294">
              {gains.map((gain, i) => <motion.li key={gain} className="flex items-start gap-3" initial={{
              opacity: 0,
              x: -20
            }} animate={isInView ? {
              opacity: 1,
              x: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.3 + i * 0.1
            }} data-id="element-295">
                  <CheckIcon className="w-5 h-5 text-tiq-green flex-shrink-0 mt-0.5" data-id="element-296" />
                  <span className="text-tiq-blue" data-id="element-297">{gain}</span>
                </motion.li>)}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}